import React, { useState } from "react";
import { Alert, Dialog, DialogContent } from "@mui/material";
import { EmailCallout } from "../../../components/VendorEmailDialog";
import { OrganizationDataRecipientDto } from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { OrganizationDto } from "../../../../common/service/server/dto/OrganizationDto";
import { Button } from "@truevault/aquilo";
import validator from "validator";
import { Api } from "../../../../common/service/Api";
import { UpdateVendorArgs } from "../components/workflows/create-data-recipient/ClassificationForDataRecipientPage";
import {
  doesGdprInternationalDataTransferApplyForVendor,
  doesGppSignalApplyForVendor,
  doesVendorHaveUsClassification,
  isCaContractFieldRequiredForVendor,
  isGdprInternationalDataTransferComplete,
  isUsClassificationRequiredForVendor,
  isVendorCaContractDocumentationComplete,
  isVendorGdprClassificationComplete,
  isVendorGppSignalDocumentationComplete,
} from "./VendorDocumentationPage";

export type VendorDocumentationProperty =
  | "us-classification"
  | "gdpr-classification"
  | "ca-contract-requirement"
  | "gpp-signal"
  | "international-data-transfer";

export const DEFAULT_VENDOR_DOCUMENTATION_EMAIL_SUBJECT = "Privacy Documentation Inquiry";

const VendorDocumentationEmailTemplates: Record<
  VendorDocumentationProperty,
  { body: string; header: string }
> = {
  "us-classification": {
    header: "US Classification",
    body: `We are trying to determine whether your company is a "processor" or "controller" of personal data it receives from us, as those terms are defined by US data privacy laws. (Also called "service provider" and "third party," under the California Consumer Privacy Act.) So far, we haven't been able to find the documentation to help us make that determination.

Could you please provide assistance by clarifying whether your company acts as a processor or controller, and indicate where I can find the required contractual language to confirm that? If you are a processor but no such language exists, would you be willing to sign a data processing agreement (DPA) with us?`,
  },
  "gdpr-classification": {
    header: "GDPR",
    body: `We are trying to determine whether your company is a "processor" or "controller" of personal data it receives from us, as those terms are defined by the GDPR. So far, we haven't been able to find the documentation to help us make that determination.

Could you please provide assistance by clarifying whether your company acts as a processor or controller, and indicate where I can find the required contractual language to confirm that? If you are a processor but no such language exists, would you be willing to sign a data processing agreement (DPA) with us?`,
  },
  "ca-contract-requirement": {
    header: "California Contract",
    body: `California's privacy law (the CCPA) requires that recipients of personal information (including service providers, contractors, and third parties) must enter into a contract that:
- States that the personal information is being disclosed for limited and specific purposes;
- Requires you to comply with applicable obligations under the CCPA;
- Grants us the right to take reasonable steps to ensure the recipient's compliance;
- Requires that you notify us if it can no longer meet its CCPA obligations; and
- Grants our business the right to take reasonable steps to stop and remediate unauthorized use of personal information.

Could you please provide assistance by clarifying whether your company has a contract covering the requirements above?`,
  },
  "gpp-signal": {
    header: "GPP",
    body: `We are trying to determine whether your company respects Global Privacy Protocol (GPP) signals for opt-outs and online consents. So far, we haven't been able to find the documentation to help us make that determination.

Could you please provide assistance by clarifying whether your company respects GPP signals, and indicate where I can find a contract or other documentation to confirm that?`,
  },
  "international-data-transfer": {
    header: "International Data Transfer",
    body: `You are one of our providers who processes personal data and we are trying to determine if you have Standard Contractual Clauses (SCCs) in place that govern our transfer of personal data to you from the EEA/UK/CH, in accordance with GDPR. If you have SCCs in place, could you please direct me to the supporting documentation?`,
  },
};

export function EmailVendorDialog({
  org,
  vendor,
  updateVendor,
  isOpen,
  closeModal,
}: {
  org: OrganizationDto;
  vendor: OrganizationDataRecipientDto;
  updateVendor: (v: UpdateVendorArgs) => Promise<void>;
  isOpen: boolean;
  closeModal: () => void;
}) {
  const [emailBody, setEmailBody] = useState(getDefaultEmailBodyForVendor(org, vendor));
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState(false);

  let vendorEmail = vendor.email || vendor.defaultEmail;

  const dispatchEmail = async (emailContent: string, subject?: string) => {
    setEmailError(false);
    setEmailLoading(true);

    try {
      const res = await Api.organizationVendor.requestVendorServiceProviderInfo(
        org.id,
        [vendor.vendorId],
        [vendorEmail],
        emailContent,
        subject,
      );
      if (res?.ok === true) {
        const currentDate = new Date().toISOString();

        await updateVendor({
          vendorId: vendor.vendorId,
          vendorSettings: {
            ...vendor,
            serviceProviderInfoRequestSentDate: currentDate,
          },
        });

        closeModal();
      } else {
        setEmailError(true);
      }
    } catch (e) {
      setEmailError(true);
    } finally {
      setEmailLoading(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => closeModal()}
      maxWidth="md"
      fullWidth
      aria-labelledby="email-dialog-title-us"
      aria-describedby="email-dialog-description-us"
    >
      <DialogContent>
        <div className="tw-boundary">
          <div className="mb-[16px] text-[18px] font-medium text-neutral-900">
            Review and send the email below. Our template addresses all missing documentation
            requirements for this vendor. You can remove and edit text before sending.
          </div>
        </div>

        <EmailCallout
          vendorEmail={vendorEmail}
          setVendorEmail={() => {}}
          emailSubject={DEFAULT_VENDOR_DOCUMENTATION_EMAIL_SUBJECT}
          emailBody={emailBody}
          setEmailBody={setEmailBody}
          emailLoading={emailLoading}
          emailSent={false}
          org={org}
        />

        {emailError && (
          <Alert className="mt-md mb-md" severity="error">
            There was an error sending the email.
          </Alert>
        )}

        <div className="tw-boundary">
          <div className="flex items-center justify-end gap-[12px]">
            <Button color="tertiary" onClick={() => closeModal()}>
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={emailLoading || !vendorEmail || !validator.isEmail(vendorEmail)}
              // disabledTooltip="Please enter a valid email address"
              onClick={() => dispatchEmail(emailBody, DEFAULT_VENDOR_DOCUMENTATION_EMAIL_SUBJECT)}
            >
              Send
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function getDefaultEmailBodyForVendor(
  org: OrganizationDto,
  vendor: OrganizationDataRecipientDto,
): string {
  let isEeaUk = org?.featureGdpr && vendor.processingRegions.includes("EEA_UK");

  // We first need to determine how many properties are missing, since that will determine whether to show the headers
  let missingVendorProperties: VendorDocumentationProperty[] = [];

  // TODO(saurabh): refactor this to avoid duplicating logic with `isVendorDocumentationClassified`

  if (isUsClassificationRequiredForVendor(vendor, org) && !doesVendorHaveUsClassification(vendor)) {
    missingVendorProperties.push("us-classification");
  }

  if (
    isCaContractFieldRequiredForVendor(vendor) &&
    !isVendorCaContractDocumentationComplete(vendor)
  ) {
    missingVendorProperties.push("ca-contract-requirement");
  }

  if (isEeaUk) {
    if (!isVendorGdprClassificationComplete(vendor)) {
      missingVendorProperties.push("gdpr-classification");
    }
    if (
      doesGdprInternationalDataTransferApplyForVendor(vendor) &&
      !isGdprInternationalDataTransferComplete(vendor)
    ) {
      missingVendorProperties.push("international-data-transfer");
    }
  }

  if (doesGppSignalApplyForVendor(vendor) && !isVendorGppSignalDocumentationComplete(vendor)) {
    missingVendorProperties.push("gpp-signal");
  }

  let body = `Hello,

We are currently reviewing all of our vendors for privacy compliance and are contacting you to ask about certain contract requirements under privacy laws.`;

  for (let property of missingVendorProperties) {
    let newText =
      missingVendorProperties.length > 1
        ? VendorDocumentationEmailTemplates[property].header + "\n\n"
        : "";
    newText += VendorDocumentationEmailTemplates[property].body;
    body += "\n\n" + newText;
  }

  return body;
}
